// Master Apps · Stacks — matriz de tecnologias × aplicações que as usam (admin).
//
// A tela é 100% derivada do array `stacks` das aplicações: nada de número
// inventado. As tecnologias são agrupadas pelas categorias do catálogo
// (stacks-catalog.ts) e o que estiver fora dele cai em "Outras" — o cadastro
// permite tecnologia livre, então esse caso é esperado.
//
// Recursos: métricas no topo, busca (tecnologia ou aplicação), filtros por
// categoria / tipo de aplicação / criticidade, ordenação (mais usadas | A-Z),
// seções colapsáveis e duas visões (por tecnologia | por aplicação).
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppPhoto, AppTypeTag } from '@/components/app-visuals';
import {
  IconAlert,
  IconChevronD,
  IconChevronR,
  IconCpu,
  IconLayers,
  IconSearch,
  IconTrend,
  IconX,
} from '@/components/icons';
import { ButtonGhost, FilterChip, Sidebar, TopBar } from '@/components/primitives';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { APP_TYPES, type App, STACK_COLORS } from '@/lib/catalog-config';
import { ALL_STACKS, STACK_CATALOG, stackCategoryOf } from '@/lib/stacks-catalog';
import {
  fontSans,
  liftForDark,
  makePalette,
  NOC_RED,
  numeroTabular,
  type Palette,
  rotuloCaixaAlta,
  type ScreenProps,
} from '@/lib/theme';

// ---------------------------------------------------------------- constantes

// Categoria-coringa para tecnologia livre (fora do catálogo). Sempre por último.
const OUTRAS = 'Outras';
// Ordem das seções = ordem de relevância do catálogo, com "Outras" ao final.
const CATEGORY_LABELS: string[] = [...STACK_CATALOG.map((c) => c.label), OUTRAS];

const CRITICALITIES = [
  { id: 'alta', label: 'Crítica', color: '#dc2626' },
  { id: 'media', label: 'Média', color: '#ca8a04' },
  { id: 'baixa', label: 'Baixa', color: '#16a34a' },
] as const;

// ─── Grades, alturas e quebras por faixa de viewport ───
// A tela é 100% estilo inline, e estilo inline vence regra de classe: nenhuma
// `@media` no CSS conseguiria trocar `gridTemplateColumns` aqui. Quem escolhe é
// `byViewport`. As tabelas moram fora dos componentes para a decisão ficar num
// lugar só e para não remontar objeto a cada render.

/**
 * Cartões de métrica. Quatro em linha só no desktop: a 375px cada um sobrava com
 * 33px de conteúdo (medido no navegador), e o número de 36px vazava a moldura —
 * eram 25 blocos com rolagem horizontal acidental só nesta fileira.
 */
const METRIC_GRID = { mobile: '1fr', tablet: '1fr 1fr', desktop: 'repeat(4, 1fr)' } as const;

/**
 * Grade dos cards de tecnologia. O `minmax(340px, 1fr)` é um PISO, não um teto:
 * quando a coluna de conteúdo tem menos que isso (272px a 320px), a trilha fica
 * com os 340px e estoura a viewport. No celular a grade vira uma coluna elástica.
 */
const TECH_GRID = {
  mobile: '1fr',
  tablet: 'repeat(auto-fill, minmax(340px, 1fr))',
  desktop: 'repeat(auto-fill, minmax(340px, 1fr))',
} as const;

/**
 * Categoria × chips dentro do card de aplicação. A coluna de rótulo de 190px
 * deixava 36px para os chips a 320px — e chip de tecnologia é `nowrap`, então
 * vazava. No celular o rótulo passa a ficar ACIMA dos chips.
 */
const APP_TECH_GRID = { mobile: '1fr', tablet: '190px 1fr', desktop: '190px 1fr' } as const;

/** Respiro do conteúdo · 24px de cada lado custam 48 dos 320px de um celular. */
const CONTENT_PAD = { mobile: 14, tablet: 18, desktop: 24 } as const;

/** Moldura dos cards · o card estreito precisa da largura de volta. */
const CARD_PAD = { mobile: 13, tablet: 16, desktop: 16 } as const;
/** Moldura do cartão de métrica · o desktop mantém os 18px de hoje. */
const TILE_PAD = { mobile: 14, tablet: 16, desktop: 18 } as const;

/** Caixa de busca · 32px no desktop, alvo de toque no compacto. */
const SEARCH_H = { mobile: 44, tablet: 40, desktop: 32 } as const;
/** Largura da busca · fixa no desktop, a linha inteira no celular. */
const SEARCH_W = { mobile: '100%', tablet: 300, desktop: 300 } as const;

/** Linha de busca + ordenação + visão · empilha no celular. */
const TOOLBAR_DIR = { mobile: 'column', tablet: 'row', desktop: 'row' } as const;
const TOOLBAR_ALIGN = { mobile: 'stretch', tablet: 'center', desktop: 'center' } as const;

/** Grupo de filtros: rótulo em coluna de 86px no desktop, linha própria no celular. */
const FACET_DIR = { mobile: 'column', tablet: 'row', desktop: 'row' } as const;
const FACET_GAP = { mobile: 6, tablet: 10, desktop: 10 } as const;
const FACET_LABEL_W = { mobile: 'auto', tablet: 86, desktop: 86 } as const;
const FACET_LABEL_MT = { mobile: 0, tablet: 9, desktop: 9 } as const;

/**
 * Texto longo (nome de tecnologia, nome de aplicação): reticências no desktop,
 * onde a linha tem de caber numa altura só; quebra de linha no celular, onde o
 * card tem 240px e o "…" comia metade do nome.
 */
const LONG_WRAP = { mobile: 'normal', tablet: 'nowrap', desktop: 'nowrap' } as const;
const LONG_OVERFLOW = { mobile: 'visible', tablet: 'hidden', desktop: 'hidden' } as const;
const LONG_BREAK = { mobile: 'anywhere', tablet: undefined, desktop: undefined } as const;
/** Cabeçalho de card: no celular os selos descem para a linha de baixo. */
const CARD_HEAD_WRAP = { mobile: 'wrap', tablet: 'nowrap', desktop: 'nowrap' } as const;

/**
 * Alvo de toque dos controles pequenos. `undefined` no desktop de propósito: a
 * propriedade nem é emitida, então o desenho de hoje fica byte a byte igual.
 */
const CHIP_MIN_H = { mobile: 40, tablet: 40, desktop: undefined } as const;
const SEGMENT_H = { mobile: 40, tablet: 32, desktop: 26 } as const;
const ICON_BTN = { mobile: 40, tablet: 32, desktop: 18 } as const;
const PILL_H = { mobile: 44, tablet: 32, desktop: 26 } as const;
/** O X da busca não tem tamanho declarado hoje (só o ícone de 12px): no desktop
 *  continua sem — quem ganha quadrado de toque é o compacto. */
const CLEAR_BTN = { mobile: 40, tablet: 32, desktop: undefined } as const;
/**
 * Cabeçalho de seção colapsável (a matriz e o catálogo de referência). São
 * botões de largura cheia, então o problema é só a altura: `padding: 8px 10px`
 * sobre uma linha de 20px dá 38px, e a sonda mediu `button 347×38` em cada
 * seção aberta a 375px — dois nesta tela, um por categoria com resultado. O
 * `undefined` no desktop não emite a propriedade: lá o botão fica byte a byte
 * como hoje.
 */
const SECTION_MIN_H = { mobile: 44, tablet: 44, desktop: undefined } as const;
const COMPACT_CENTER = { mobile: 'center', tablet: 'center', desktop: undefined } as const;
const COMPACT_FIRM = { mobile: 0, tablet: 0, desktop: undefined } as const;
/** Em coluna a fileira de chips tem de esticar; em linha ela segue o `flex-start`
 *  do container, exatamente como hoje. */
const COMPACT_STRETCH = { mobile: 'stretch', tablet: 'flex-start', desktop: undefined } as const;

const DIACRITIC = /\p{Diacritic}/gu;

/** Minúsculas sem acento — busca e deduplicação tolerantes a caixa/acento. */
function norm(value: string): string {
  return value.normalize('NFD').replace(DIACRITIC, '').toLowerCase().trim();
}

// Nome normalizado -> grafia canônica do catálogo. Se duas apps gravarem
// "react" e "React", a matriz mostra uma linha só, com a grafia oficial.
const CANONICAL_STACK = new Map<string, string>(ALL_STACKS.map((name) => [norm(name), name]));

function stackColor(name: string, dark: boolean): string {
  return liftForDark(STACK_COLORS[name] || '#64748b', dark, 0.52);
}

function criticalityLabel(id: string): string {
  return CRITICALITIES.find((c) => c.id === id)?.label ?? id;
}

// ------------------------------------------------------------------- modelos

interface TechEntry {
  /** Grafia canônica (do catálogo quando existir). */
  name: string;
  /** Label da categoria, ou "Outras" para tecnologia livre. */
  category: string;
  /** Aplicações que usam a tecnologia. */
  apps: App[];
}

interface TechRow {
  tech: TechEntry;
  /** Aplicações exibidas como chips (após busca). */
  shown: App[];
  /** Aplicações que usam a tecnologia após os filtros de aplicação. */
  total: number;
}

type SortMode = 'usage' | 'alpha';
type ViewMode = 'tech' | 'app';

/** Uma aplicação com as tecnologias dela, na visão inversa da matriz. */
interface AppTechRow {
  app: App;
  techs: TechEntry[];
}

/** Agregação base da tela, sem nenhum filtro aplicado. */
interface StacksBase {
  techs: TechEntry[];
  byApp: Map<string, TechEntry[]>;
  max: number;
  top: TechEntry | null;
  links: number;
  singles: number;
  categoriesUsed: number;
}

/** O que sobra depois da busca, dos filtros e da ordenação. */
interface StacksResult {
  rows: TechRow[];
  visible: TechRow[];
  sections: { label: string; rows: TechRow[] }[];
  links: number;
  appRows: AppTechRow[];
  categoryCounts: Map<string, number>;
  categoryTotal: number;
  typeCounts: Map<string, number>;
  typeTotal: number;
  criticalityCounts: Map<string, number>;
  criticalityTotal: number;
}

/** Filtros de aplicação (tipo e criticidade) que valem nas duas visões. */
interface AppFacets {
  type: string;
  criticality: string;
}

// ---------------------------------------------------------------- agregação

/**
 * Percorre o cadastro uma única vez e monta a matriz tecnologia × aplicações,
 * deduplicando por nome normalizado e resolvendo a grafia canônica. Devolve
 * também as métricas do topo (mais usada, vínculos, uso único, categorias).
 */
/**
 * Tecnologias declaradas pela aplicação, na ordem do cadastro, sem repetição:
 * `["React", "react ", ""]` rende uma única entrada. A chave normalizada viaja
 * junto do nome cru porque é ela que resolve a grafia canônica depois.
 */
function stackKeysOf(app: App): { key: string; name: string }[] {
  const out: { key: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const raw of app.stacks ?? []) {
    const name = (raw ?? '').trim();
    const key = norm(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, name });
  }
  return out;
}

/** Entrada da tecnologia no índice, criada na primeira aparição. */
function techEntryOf(byKey: Map<string, TechEntry>, key: string, name: string): TechEntry {
  const existing = byKey.get(key);
  if (existing) return existing;
  // Primeira aparição decide a grafia exibida: a canônica do catálogo quando
  // existe, senão o nome como foi cadastrado.
  const canonical = CANONICAL_STACK.get(key) ?? name;
  const entry: TechEntry = {
    name: canonical,
    category: stackCategoryOf(canonical) ?? OUTRAS,
    apps: [],
  };
  byKey.set(key, entry);
  return entry;
}

/** Métricas do topo da tela, numa passada sobre a matriz já agregada. */
function metricsOf(techs: TechEntry[]): Pick<StacksBase, 'max' | 'top' | 'links' | 'singles'> {
  let max = 0;
  let top: TechEntry | null = null;
  let links = 0;
  let singles = 0;
  for (const t of techs) {
    links += t.apps.length;
    if (t.apps.length === 1) singles += 1;
    if (t.apps.length > max) {
      max = t.apps.length;
      top = t;
    }
  }
  return { max, top, links, singles };
}

function aggregateStacks(apps: App[]): StacksBase {
  const byKey = new Map<string, TechEntry>();
  const byApp = new Map<string, TechEntry[]>();
  for (const a of apps) {
    for (const { key, name } of stackKeysOf(a)) {
      const entry = techEntryOf(byKey, key, name);
      entry.apps.push(a);
      const list = byApp.get(a.id);
      if (list) list.push(entry);
      else byApp.set(a.id, [entry]);
    }
  }
  const techs = [...byKey.values()];
  return {
    techs,
    byApp,
    ...metricsOf(techs),
    categoriesUsed: new Set(techs.map((t) => t.category)).size,
  };
}

// ------------------------------------------------------------------ filtros

/** Linhas de tecnologia após a busca e os filtros de aplicação. */
function techRowsOf(
  base: StacksBase,
  q: string,
  passesAppFacets: (a: App) => boolean,
  appHitsQuery: (a: App) => boolean,
): TechRow[] {
  const rows: TechRow[] = [];
  for (const tech of base.techs) {
    const pool = tech.apps.filter(passesAppFacets);
    if (pool.length === 0) continue;
    const techHit = !q || norm(tech.name).includes(q);
    const shown = techHit ? pool : pool.filter(appHitsQuery);
    if (shown.length === 0) continue;
    rows.push({ tech, shown, total: pool.length });
  }
  return rows;
}

/** Visão inversa: aplicações com suas tecnologias, já filtradas e ordenadas. */
function appRowsOf(
  apps: App[],
  base: StacksBase,
  q: string,
  category: string,
  sort: SortMode,
  passesAppFacets: (a: App) => boolean,
  appHitsQuery: (a: App) => boolean,
): AppTechRow[] {
  const appRows: AppTechRow[] = [];
  for (const a of apps) {
    if (!passesAppFacets(a)) continue;
    const owned = base.byApp.get(a.id) ?? [];
    const appHit = !q || appHitsQuery(a);
    let techs = category === 'all' ? owned : owned.filter((t) => t.category === category);
    if (q && !appHit) techs = techs.filter((t) => norm(t.name).includes(q));
    // App sem tecnologia só aparece na listagem limpa (é um sinal de auditoria).
    if (techs.length === 0 && (q || category !== 'all')) continue;
    appRows.push({ app: a, techs });
  }
  appRows.sort(
    sort === 'usage'
      ? (a, b) => b.techs.length - a.techs.length || a.app.name.localeCompare(b.app.name, 'pt-BR')
      : (a, b) => a.app.name.localeCompare(b.app.name, 'pt-BR'),
  );
  return appRows;
}

/** Facetas de aplicação: contam apps COM tecnologia, cruzando o outro filtro. */
function appFacetCounts(apps: App[], base: StacksBase, facets: AppFacets) {
  const typeCounts = new Map<string, number>();
  const criticalityCounts = new Map<string, number>();
  let typeTotal = 0;
  let criticalityTotal = 0;
  for (const a of apps) {
    if ((base.byApp.get(a.id)?.length ?? 0) === 0) continue;
    if (facets.criticality === 'all' || a.criticality === facets.criticality) {
      typeCounts.set(a.type, (typeCounts.get(a.type) ?? 0) + 1);
      typeTotal += 1;
    }
    if (facets.type === 'all' || a.type === facets.type) {
      criticalityCounts.set(a.criticality, (criticalityCounts.get(a.criticality) ?? 0) + 1);
      criticalityTotal += 1;
    }
  }
  return { typeCounts, typeTotal, criticalityCounts, criticalityTotal };
}

/** Busca + facetas + categoria + ordenação, nas duas visões da tela. */
function filterStacks(
  apps: App[],
  base: StacksBase,
  query: string,
  category: string,
  facets: AppFacets,
  sort: SortMode,
): StacksResult {
  const q = norm(query);
  const passesAppFacets = (a: App) =>
    (facets.type === 'all' || a.type === facets.type) &&
    (facets.criticality === 'all' || a.criticality === facets.criticality);
  const appHitsQuery = (a: App) =>
    norm(a.name).includes(q) || norm(a.slug).includes(q) || norm(a.team ?? '').includes(q);

  // Linhas de tecnologia (ignoram o filtro de categoria — servem de faceta).
  const rows = techRowsOf(base, q, passesAppFacets, appHitsQuery);

  const categoryCounts = new Map<string, number>();
  for (const r of rows) {
    categoryCounts.set(r.tech.category, (categoryCounts.get(r.tech.category) ?? 0) + 1);
  }

  const visible = category === 'all' ? rows : rows.filter((r) => r.tech.category === category);
  const byName = (a: TechRow, b: TechRow) => a.tech.name.localeCompare(b.tech.name, 'pt-BR');
  const sorted = [...visible].sort(
    sort === 'usage' ? (a, b) => b.total - a.total || byName(a, b) : byName,
  );
  const sections = CATEGORY_LABELS.map((label) => ({
    label,
    rows: sorted.filter((r) => r.tech.category === label),
  })).filter((s) => s.rows.length > 0);
  const links = visible.reduce((sum, r) => sum + r.total, 0);

  return {
    rows,
    visible,
    sections,
    links,
    appRows: appRowsOf(apps, base, q, category, sort, passesAppFacets, appHitsQuery),
    categoryCounts,
    categoryTotal: rows.length,
    ...appFacetCounts(apps, base, facets),
  };
}

/** Alterna a presença de uma chave num conjunto imutável de seções abertas. */
function toggleKey(current: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Qual dos três estados a tela mostra: falha, base sem stacks, ou a matriz. */
function stacksState(error: string | null, techCount: number): 'error' | 'empty' | 'matrix' {
  if (error) return 'error';
  if (techCount === 0) return 'empty';
  return 'matrix';
}

/** Subtítulo do TopBar, em três variantes conforme o estado da tela. */
function stacksSubtitle(error: string | null, base: StacksBase, appCount: number): string {
  if (error) return 'Não foi possível carregar as tecnologias';
  if (base.techs.length === 0) {
    const unit = appCount === 1 ? 'aplicação cadastrada' : 'aplicações cadastradas';
    return `${appCount} ${unit}`;
  }
  const unit = appCount === 1 ? 'aplicação' : 'aplicações';
  return `${base.techs.length} tecnologias em ${appCount} ${unit} · ${base.categoriesUsed} categorias`;
}

// ------------------------------------------------------- peças de UI locais

function SectionLabel({ children, p }: Readonly<{ children: string; p: Palette }>) {
  const vp = useViewport();
  return (
    <div
      style={{
        // No celular o rótulo deixa de ser coluna e passa a ser a linha de cima
        // dos chips: 86px de gaveta reservada custavam um terço da tela a 320px.
        width: byViewport(vp, FACET_LABEL_W),
        flexShrink: 0,
        marginTop: byViewport(vp, FACET_LABEL_MT),
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: p.muted,
      }}
    >
      {children}
    </div>
  );
}

function MetricTile({
  dark,
  label,
  icon,
  iconColor,
  children,
  sub,
}: Readonly<{
  dark: boolean;
  label: string;
  icon: React.ReactNode;
  iconColor: string;
  children: React.ReactNode;
  sub: string;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        padding: byViewport(vp, TILE_PAD),
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 10,
          fontWeight: 700,
          color: p.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 10,
        }}
      >
        <span style={{ color: iconColor, display: 'flex' }}>{icon}</span>
        {label}
      </div>
      {children}
      <div style={{ fontSize: 11, color: p.muted, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

function MetricNumber({ value, p }: Readonly<{ value: number; p: Palette }>) {
  return (
    <div
      style={{
        ...numeroTabular,
        fontSize: 36,
        fontWeight: 600,
        color: p.text,
        letterSpacing: '-0.025em',
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  dark,
}: Readonly<{
  options: readonly { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  dark: boolean;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        // No celular o segmentado deixa de ser `inline-flex` e ocupa a linha
        // inteira (a barra de filtros vira coluna), com os botões dividindo o
        // espaço em partes iguais — em vez de dois controles apertados no canto.
        display: byViewport(vp, { mobile: 'flex', tablet: 'inline-flex', desktop: 'inline-flex' }),
        padding: 3,
        background: p.surface2,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        gap: 2,
      }}
    >
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            style={{
              // 26px de altura é metade do alvo de toque recomendado.
              height: byViewport(vp, SEGMENT_H),
              flex: byViewport(vp, { mobile: 1, tablet: undefined, desktop: undefined }),
              padding: '0 12px',
              background: active ? p.surface : 'transparent',
              border: 'none',
              borderRadius: 6,
              fontSize: 11.5,
              fontWeight: 600,
              color: active ? p.text : p.textMuted,
              fontFamily: fontSans,
              cursor: active ? 'default' : 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.10)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pílula de filtro ativo, com X para remover só aquele filtro. */
function ActivePill({
  dark,
  accent,
  label,
  value,
  onClear,
}: Readonly<{
  dark: boolean;
  accent: string;
  label: string;
  value: string;
  onClear: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Fora do template para não aninhar literais: no escuro a borda vem do accent.
  const borderColor = dark ? `${accent}55` : '#fecaca';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        height: byViewport(vp, PILL_H),
        padding: '0 6px 0 10px',
        borderRadius: 999,
        background: dark ? `${accent}1f` : '#fef2f2',
        border: `1px solid ${borderColor}`,
        fontSize: 11.5,
        color: p.text,
        whiteSpace: 'nowrap',
        // 260px de teto passavam dos 242px que sobram a 320px: no celular o teto
        // é a própria linha.
        maxWidth: byViewport(vp, { mobile: '100%', tablet: 260, desktop: 260 }),
      }}
    >
      <span style={{ color: p.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>
        {label.toUpperCase()}
      </span>
      <span
        style={{
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={onClear}
        title={`Remover filtro ${label}`}
        aria-label={`Remover filtro ${label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          // O X de 18px é alvo de precisão: no toque vira quadrado de 40px.
          width: byViewport(vp, ICON_BTN),
          height: byViewport(vp, ICON_BTN),
          // Só no compacto: o quadrado de toque não pode encolher junto com o
          // texto do filtro. No desktop a propriedade não é emitida.
          flexShrink: byViewport(vp, COMPACT_FIRM),
          borderRadius: 999,
          border: 'none',
          background: 'transparent',
          color: p.muted,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <IconX size={11} />
      </button>
    </span>
  );
}

/** Chip de aplicação — navega para o detalhe (comportamento preservado). */
function AppChip({
  app,
  dark,
  accent,
  onClick,
}: Readonly<{
  app: App;
  dark: boolean;
  accent: string;
  onClick: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Abrir ${app.name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px 5px 5px',
        // Chip de 32px de altura é o alvo de toque mais numeroso da tela (um por
        // vínculo tecnologia × aplicação): no compacto sobe para 40px.
        minHeight: byViewport(vp, CHIP_MIN_H),
        borderRadius: 999,
        background: p.surface2,
        border: `1px solid ${p.border}`,
        cursor: 'pointer',
        color: p.text,
        fontFamily: fontSans,
        fontSize: 12,
        transition: 'border-color 140ms, background 140ms',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.borderColor = accent;
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.borderColor = p.border;
      }}
    >
      <AppPhoto app={app} size={20} dark={dark} />
      {app.name}
    </button>
  );
}

/** Chip de tecnologia (visão por aplicação) — filtra a matriz por ela. */
function TechChip({
  name,
  dark,
  accent,
  onClick,
}: Readonly<{
  name: string;
  dark: boolean;
  accent: string;
  onClick: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Ver aplicações que usam ${name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px 3px 6px',
        // O chip tem 21px de altura no desktop — no toque isso é intocável.
        minHeight: byViewport(vp, CHIP_MIN_H),
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        background: p.surface2,
        color: p.text,
        border: `1px solid ${p.border}`,
        fontFamily: fontSans,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.borderColor = accent;
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.borderColor = p.border;
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 2,
          background: stackColor(name, dark),
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
        }}
      />
      {name}
    </button>
  );
}

/** Estado vazio / de erro — moldura tracejada centralizada. */
// Catálogo de referência, exibido quando nenhuma aplicação informou tecnologia.
// É navegável de propósito: com a base vazia a tela continua útil para consultar
// o que existe no catálogo, deixando claro que ainda não há uso registrado.
function CatalogBrowser({ dark }: Readonly<{ dark: boolean }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () => new Set([STACK_CATALOG[0]?.id ?? '']),
  );
  const [query, setQuery] = useState('');
  const q = query
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
  const norm = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  const sections = STACK_CATALOG.map((c) => ({
    ...c,
    hits: q ? c.items.filter((i) => norm(i).includes(q)) : c.items,
  })).filter((c) => c.hits.length > 0);

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: p.text }}>Catálogo de referência</div>
        <div style={{ fontSize: 11.5, color: p.muted }}>
          {ALL_STACKS.length} tecnologias disponíveis no cadastro, agrupadas por categoria
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: byViewport(vp, SEARCH_H),
            padding: '0 10px',
            // 240px fixos não cabem nos 242px úteis de um celular de 320px junto
            // com o resto da linha: no compacto a busca toma a linha inteira.
            width: byViewport(vp, { mobile: '100%', tablet: 240, desktop: 240 }),
            background: p.surface,
            border: `1px solid ${p.border}`,
            borderRadius: 8,
            color: p.muted,
          }}
        >
          <IconSearch size={13} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar tecnologia…"
            style={{
              flex: 1,
              minWidth: 0,
              // Esticar o campo faz a área de clique valer a caixa inteira.
              alignSelf: byViewport(vp, { mobile: 'stretch', tablet: 'stretch', desktop: 'auto' }),
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: p.text,
              fontSize: 12,
              fontFamily: fontSans,
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Limpar busca"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: p.muted,
                display: 'inline-flex',
                // Sem tamanho declarado este X é do tamanho do ícone (12px). No
                // compacto ele ganha o mesmo quadrado de toque do X da busca da
                // matriz; no desktop as três propriedades não são emitidas e o
                // botão continua colado no ícone, como hoje.
                alignItems: byViewport(vp, COMPACT_CENTER),
                justifyContent: byViewport(vp, COMPACT_CENTER),
                width: byViewport(vp, CLEAR_BTN),
                height: byViewport(vp, CLEAR_BTN),
                flexShrink: byViewport(vp, COMPACT_FIRM),
                // `undefined` no desktop de novo, e não `0`: sem declaração o
                // navegador aplica o padding padrão de <button> (1px 6px), que é
                // o desenho de hoje. Zerar aqui mudaria a caixa no desktop.
                padding: byViewport<number | undefined>(vp, {
                  mobile: 0,
                  tablet: 0,
                  desktop: undefined,
                }),
              }}
            >
              <IconX size={12} />
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sections.map((c) => {
          const expanded = !!q || open.has(c.id);
          return (
            <div
              key={c.id}
              style={{
                background: p.surface,
                border: `1px solid ${p.border}`,
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={q ? undefined : () => setOpen((prev) => toggleKey(prev, c.id))}
                aria-expanded={expanded}
                style={{
                  width: '100%',
                  // Mesmo motivo do cabeçalho da matriz: 10+10 de respiro sobre
                  // uma linha de 18px dá 38px de alvo no celular.
                  minHeight: byViewport(vp, SECTION_MIN_H),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: p.surface2,
                  border: 'none',
                  cursor: q ? 'default' : 'pointer',
                  textAlign: 'left',
                  color: p.text,
                  fontFamily: fontSans,
                }}
              >
                <span style={{ color: p.muted, display: 'inline-flex' }}>
                  {expanded ? <IconChevronD size={13} /> : <IconChevronR size={13} />}
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.label}</span>
                <span style={{ fontSize: 11, color: p.muted, ...numeroTabular }}>
                  {c.hits.length}
                </span>
                <div style={{ flex: 1 }} />
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  {c.hits.slice(0, 8).map((t) => (
                    <span
                      key={t}
                      title={t}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 2,
                        background: liftForDark(STACK_COLORS[t] ?? '#475569', dark),
                      }}
                    />
                  ))}
                </span>
              </button>
              {expanded && (
                <div style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {c.hits.map((t) => (
                    <span
                      key={t}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 9px',
                        borderRadius: 5,
                        fontSize: 11.5,
                        background: p.surface2,
                        border: `1px solid ${p.border}`,
                        color: p.textMuted,
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 2,
                          background: liftForDark(STACK_COLORS[t] ?? '#475569', dark),
                        }}
                      />
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {sections.length === 0 && (
          <div
            style={{
              padding: '22px 14px',
              textAlign: 'center',
              fontSize: 12,
              color: p.muted,
              border: `1px dashed ${p.borderStrong}`,
              borderRadius: 8,
            }}
          >
            Nenhuma tecnologia do catálogo corresponde a "{query}". O cadastro também aceita
            tecnologia livre.
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: p.muted }}>
        Nenhuma aplicação usa estas tecnologias ainda. Os grupos com contagem de uso, filtros e
        métricas aparecem aqui assim que a primeira aplicação for cadastrada com o campo Tecnologias
        preenchido.
      </div>
    </div>
  );
}

function EmptyState({
  dark,
  accent,
  icon,
  title,
  text,
  actionLabel,
  onAction,
  actionBusy = false,
  tone = 'neutral',
}: Readonly<{
  dark: boolean;
  accent: string;
  icon: React.ReactNode;
  title: string;
  text: string;
  actionLabel?: string;
  onAction?: () => void;
  actionBusy?: boolean;
  tone?: 'neutral' | 'danger';
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const iconColor = tone === 'danger' ? '#dc2626' : accent;
  return (
    <div
      style={{
        border: `1px dashed ${p.borderStrong}`,
        borderRadius: 12,
        background: p.surface,
        // 32px de moldura de cada lado num celular de 320px sobram 208px para um
        // texto de duas frases: no compacto o respiro cede espaço ao recado.
        padding: byViewport(vp, {
          mobile: '34px 16px',
          tablet: '44px 24px',
          desktop: '52px 32px',
        }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 6,
      }}
    >
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: dark ? `${iconColor}1f` : `${iconColor}14`,
          color: iconColor,
          marginBottom: 8,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: p.text, letterSpacing: '-0.012em' }}>
        {title}
      </div>
      <div style={{ fontSize: 12.5, color: p.muted, maxWidth: 440, lineHeight: 1.6 }}>{text}</div>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          disabled={actionBusy}
          style={{
            marginTop: 12,
            // Único botão do estado vazio (e do estado de erro): no celular é a
            // saída da tela e não pode ficar nos 34px de antes.
            height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 34 }),
            padding: '0 16px',
            background: accent,
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: fontSans,
            cursor: actionBusy ? 'default' : 'pointer',
            opacity: actionBusy ? 0.6 : 1,
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** Card de uma tecnologia: cor, nome, contagem, barra de proporção e chips. */
function TechCard({
  row,
  max,
  dark,
  accent,
  onOpenApp,
}: Readonly<{
  row: TechRow;
  max: number;
  dark: boolean;
  accent: string;
  onOpenApp: (a: App) => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const color = stackColor(row.tech.name, dark);
  const share = max > 0 ? Math.max(4, Math.round((row.total / max) * 100)) : 0;
  const hidden = row.total - row.shown.length;
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        padding: byViewport(vp, CARD_PAD),
        minWidth: 0,
        transition: 'border-color 140ms',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = p.borderStrong;
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = p.border;
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 0,
          // No celular o selo "Uso único" e o contador descem para a linha de
          // baixo em vez de espremerem o nome da tecnologia até as reticências.
          flexWrap: byViewport(vp, CARD_HEAD_WRAP),
        }}
      >
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: color,
            flexShrink: 0,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
          }}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: p.text,
            flex: 1,
            minWidth: 0,
            fontFamily: fontSans,
            overflow: byViewport(vp, LONG_OVERFLOW),
            textOverflow: 'ellipsis',
            // Nome de tecnologia é o dado principal do card: no celular ele quebra
            // em duas linhas em vez de sumir atrás do "…".
            whiteSpace: byViewport(vp, LONG_WRAP),
            // Nome de tecnologia não tem espaço para quebrar ("ContinuousDelivery"):
            // sem `anywhere` a palavra única continuaria vazando o card.
            overflowWrap: byViewport(vp, LONG_BREAK),
          }}
        >
          {row.tech.name}
        </span>
        {row.total === 1 && (
          <span
            style={{
              ...rotuloCaixaAlta,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#ca8a04',
              background: dark ? '#ca8a0422' : '#fef9c3',
              border: `1px solid ${dark ? '#ca8a0444' : '#fde68a'}`,
              borderRadius: 3,
              padding: '2px 6px',
              whiteSpace: 'nowrap',
            }}
            title="Só uma aplicação usa esta tecnologia"
          >
            Uso único
          </span>
        )}
        <span
          style={{
            ...numeroTabular,
            fontSize: 11,
            fontWeight: 700,
            color: p.textMuted,
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 999,
            padding: '2px 9px',
            flexShrink: 0,
          }}
          title={`${row.total} ${row.total === 1 ? 'aplicação usa' : 'aplicações usam'} ${row.tech.name}`}
        >
          {row.total}
        </span>
      </div>

      {/* Proporção de uso relativa à tecnologia mais usada */}
      {max > 1 && (
        <div
          style={{
            height: 4,
            borderRadius: 999,
            background: p.surface3,
            marginTop: 12,
            overflow: 'hidden',
          }}
        >
          <div style={{ width: `${share}%`, height: '100%', background: color }} />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${p.border}`,
        }}
      >
        {row.shown.map((a) => (
          <AppChip key={a.id} app={a} dark={dark} accent={accent} onClick={() => onOpenApp(a)} />
        ))}
        {hidden > 0 && (
          <span
            style={{
              ...numeroTabular,
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 11,
              color: p.muted,
            }}
          >
            +{hidden} fora da busca
          </span>
        )}
      </div>
    </div>
  );
}

/** Aplicações distintas e vínculos de uma seção — o resumo do cabeçalho. */
function sectionTotals(rows: TechRow[]): { apps: number; links: number } {
  const apps = new Set<string>();
  let links = 0;
  for (const r of rows) {
    links += r.total;
    for (const a of r.shown) apps.add(a.id);
  }
  return { apps: apps.size, links };
}

/** Uma categoria da matriz: cabeçalho colapsável + grade de TechCards. */
function TechSection({
  section,
  max,
  dark,
  accent,
  searching,
  collapsed,
  onToggle,
  onOpenApp,
}: Readonly<{
  section: { label: string; rows: TechRow[] };
  max: number;
  dark: boolean;
  accent: string;
  searching: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onOpenApp: (a: App) => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const totals = sectionTotals(section.rows);
  return (
    <div>
      <button
        type="button"
        onClick={searching ? undefined : onToggle}
        aria-expanded={!collapsed}
        style={{
          width: '100%',
          // Único alvo de "recolher/expandir a categoria" e o mais alto da lista:
          // a sonda mediu 347×38 a 375px. `minHeight` (não `height`) porque o
          // resumo da seção embrulha em duas linhas no celular e a moldura tem de
          // acompanhar.
          minHeight: byViewport(vp, SECTION_MIN_H),
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          marginBottom: 10,
          background: p.surface2,
          border: `1px solid ${p.border}`,
          borderRadius: 8,
          cursor: searching ? 'default' : 'pointer',
          textAlign: 'left',
          fontFamily: fontSans,
          color: p.text,
        }}
      >
        <span style={{ color: p.muted, display: 'flex' }}>
          {collapsed ? <IconChevronR size={14} /> : <IconChevronD size={14} />}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.005em',
          }}
        >
          {section.label}
        </span>
        <span
          style={{
            ...numeroTabular,
            fontSize: 10.5,
            color: p.muted,
          }}
        >
          {section.rows.length} {section.rows.length === 1 ? 'tecnologia' : 'tecnologias'} ·{' '}
          {totals.apps} {totals.apps === 1 ? 'app' : 'apps'} · {totals.links} vínculos
        </span>
        <div style={{ flex: 1 }} />
        {/* Prévia de cores quando a seção está recolhida */}
        {collapsed && (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {section.rows.slice(0, 8).map((r) => (
              <span
                key={r.tech.name}
                title={r.tech.name}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: stackColor(r.tech.name, dark),
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                }}
              />
            ))}
          </span>
        )}
      </button>
      {!collapsed && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: byViewport(vp, TECH_GRID),
            gap: byViewport(vp, { mobile: 10, tablet: 14, desktop: 14 }),
          }}
        >
          {section.rows.map((row) => (
            <TechCard
              key={row.tech.name}
              row={row}
              max={max}
              dark={dark}
              accent={accent}
              onOpenApp={onOpenApp}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Visão por tecnologia: uma seção por categoria, ou o estado sem resultado. */
function TechMatrix({
  sections,
  max,
  dark,
  accent,
  searching,
  collapsedLabels,
  onToggleSection,
  onClearFilters,
  onOpenApp,
}: Readonly<{
  sections: { label: string; rows: TechRow[] }[];
  max: number;
  dark: boolean;
  accent: string;
  searching: boolean;
  collapsedLabels: ReadonlySet<string>;
  onToggleSection: (label: string) => void;
  onClearFilters: () => void;
  onOpenApp: (a: App) => void;
}>) {
  if (sections.length === 0) {
    return (
      <EmptyState
        dark={dark}
        accent={accent}
        icon={<IconSearch size={20} />}
        title="Nenhuma tecnologia corresponde aos filtros"
        text="Tente outro termo de busca ou remova alguns filtros para ver a matriz completa."
        actionLabel="Limpar filtros"
        onAction={onClearFilters}
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {sections.map((section) => (
        <TechSection
          key={section.label}
          section={section}
          max={max}
          dark={dark}
          accent={accent}
          searching={searching}
          // Durante a busca as seções ficam sempre abertas (o resultado não
          // pode se esconder atrás de um colapso).
          collapsed={!searching && collapsedLabels.has(section.label)}
          onToggle={() => onToggleSection(section.label)}
          onOpenApp={onOpenApp}
        />
      ))}
    </div>
  );
}

/** Card de uma aplicação com as tecnologias dela agrupadas por categoria. */
function AppTechCard({
  app,
  techs,
  dark,
  accent,
  onOpenApp,
  onPickTech,
}: Readonly<{
  app: App;
  techs: TechEntry[];
  dark: boolean;
  accent: string;
  onOpenApp: () => void;
  onPickTech: (name: string) => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const groups = CATEGORY_LABELS.map((label) => ({
    label,
    items: techs.filter((t) => t.category === label),
  })).filter((g) => g.items.length > 0);
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        padding: byViewport(vp, CARD_PAD),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: byViewport(vp, { mobile: 8, tablet: 12, desktop: 12 }),
          paddingBottom: 12,
          marginBottom: 12,
          borderBottom: `1px solid ${p.border}`,
          // Nome + tipo + time + contador não caberiam numa linha de 240px.
          flexWrap: byViewport(vp, CARD_HEAD_WRAP),
        }}
      >
        <button
          type="button"
          onClick={onOpenApp}
          title={`Abrir ${app.name}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: p.text,
            fontFamily: fontSans,
            minWidth: 0,
            // No celular o nome fica com a primeira linha inteira e o resto do
            // cabeçalho (tipo, time, contador) desce — sem isso o `flex: 1` do
            // espaçador reduzia o nome a duas letras e reticências.
            flexBasis: byViewport(vp, { mobile: '100%', tablet: 'auto', desktop: 'auto' }),
            minHeight: byViewport(vp, CHIP_MIN_H),
          }}
        >
          <AppPhoto app={app} size={30} dark={dark} />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              overflow: byViewport(vp, LONG_OVERFLOW),
              textOverflow: 'ellipsis',
              whiteSpace: byViewport(vp, LONG_WRAP),
              overflowWrap: byViewport(vp, LONG_BREAK),
              textAlign: 'left',
            }}
          >
            {app.name}
          </span>
        </button>
        <AppTypeTag type={app.type} dark={dark} />
        <span
          style={{
            fontSize: 10.5,
            fontFamily: fontSans,
            color: p.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // O time é o dado mais sacrificável da linha: no celular ele encolhe
            // até as reticências para o selo de tipo e o contador ficarem inteiros.
            minWidth: byViewport(vp, { mobile: 0, tablet: undefined, desktop: undefined }),
          }}
        >
          {app.team}
        </span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            ...numeroTabular,
            fontSize: 11,
            fontWeight: 700,
            color: p.textMuted,
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 999,
            padding: '2px 9px',
          }}
        >
          {techs.length}
        </span>
      </div>
      {groups.length === 0 ? (
        <div style={{ fontSize: 12, color: p.muted }}>
          Nenhuma tecnologia informada no cadastro.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: byViewport(vp, APP_TECH_GRID),
            gap: byViewport(vp, { mobile: '6px 0', tablet: '10px 14px', desktop: '10px 14px' }),
            alignItems: 'start',
          }}
        >
          {groups.map((g) => (
            <div key={g.label} style={{ display: 'contents' }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: p.muted,
                  // Sem a coluna de 190px, o rótulo vira cabeçalho da lista de
                  // baixo: precisa de mais ar acima para separar um grupo do outro.
                  paddingTop: byViewport(vp, { mobile: 10, tablet: 4, desktop: 4 }),
                }}
              >
                {g.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {g.items.map((t) => (
                  <TechChip
                    key={t.name}
                    name={t.name}
                    dark={dark}
                    accent={accent}
                    onClick={() => onPickTech(t.name)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Visão por aplicação: um card por app, ou o estado sem resultado. */
function AppMatrix({
  rows,
  dark,
  accent,
  onClearFilters,
  onOpenApp,
  onPickTech,
}: Readonly<{
  rows: AppTechRow[];
  dark: boolean;
  accent: string;
  onClearFilters: () => void;
  onOpenApp: (a: App) => void;
  onPickTech: (name: string) => void;
}>) {
  if (rows.length === 0) {
    return (
      <EmptyState
        dark={dark}
        accent={accent}
        icon={<IconSearch size={20} />}
        title="Nenhuma aplicação corresponde aos filtros"
        text="Tente outro termo de busca ou remova alguns filtros para ver todas as aplicações e suas tecnologias."
        actionLabel="Limpar filtros"
        onAction={onClearFilters}
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {rows.map(({ app, techs }) => (
        <AppTechCard
          key={app.id}
          app={app}
          techs={techs}
          dark={dark}
          accent={accent}
          onOpenApp={() => onOpenApp(app)}
          onPickTech={onPickTech}
        />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------- tela

export function ScreenStacks({
  dark,
  accent = NOC_RED,
  apps,
  error = null,
  onRetry,
  retrying = false,
}: Readonly<
  ScreenProps & {
    apps: App[];
    /** Mensagem de falha no carregamento — a página passa quando a query quebra. */
    error?: string | null;
    onRetry?: () => void;
    retrying?: boolean;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [type, setType] = useState('all');
  const [criticality, setCriticality] = useState('all');
  const [sort, setSort] = useState<SortMode>('usage');
  const [view, setView] = useState<ViewMode>('tech');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set<string>());

  // -------------------------------------------------- agregação base (sem filtros)
  const base = useMemo(() => aggregateStacks(apps), [apps]);

  // -------------------------------------------------- filtros + facetas + ordenação
  const result = useMemo(
    () => filterStacks(apps, base, query, category, { type, criticality }, sort),
    [apps, base, query, category, type, criticality, sort],
  );

  const searching = query.trim().length > 0;
  const hasFilters = searching || category !== 'all' || type !== 'all' || criticality !== 'all';
  const clearFilters = () => {
    setQuery('');
    setCategory('all');
    setType('all');
    setCriticality('all');
  };
  const toggleSection = (label: string) => {
    setCollapsed((prev) => toggleKey(prev, label));
  };
  const allCollapsed =
    result.sections.length > 0 && result.sections.every((s) => collapsed.has(s.label));

  const state = stacksState(error, base.techs.length);
  const subtitle = stacksSubtitle(error, base, apps.length);

  const typeChips = Object.keys(APP_TYPES).filter((id) => (result.typeCounts.get(id) ?? 0) > 0);
  const criticalityChips = CRITICALITIES.filter(
    (c) => (result.criticalityCounts.get(c.id) ?? 0) > 0,
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="stacks" dark={dark} accent={accent} role="admin" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar dark={dark} title="Stacks" subtitle={subtitle} />

        <div
          style={{ flex: 1, padding: byViewport(vp, CONTENT_PAD), overflow: 'auto', minHeight: 0 }}
        >
          {state === 'error' && (
            <EmptyState
              dark={dark}
              accent={accent}
              tone="danger"
              icon={<IconAlert size={22} />}
              title="Falha ao carregar as tecnologias"
              text={error ?? ''}
              actionLabel={retrying ? 'Tentando…' : 'Tentar novamente'}
              onAction={onRetry}
              actionBusy={retrying}
            />
          )}
          {state === 'empty' && (
            <>
              {/* Base vazia (ou sem stacks informadas): convida ao cadastro. */}
              <EmptyState
                dark={dark}
                accent={accent}
                icon={<IconCpu size={22} />}
                title={
                  apps.length === 0
                    ? 'Nenhuma tecnologia mapeada ainda'
                    : 'As aplicações ainda não informaram tecnologias'
                }
                text={
                  apps.length === 0
                    ? 'A matriz de stacks é montada automaticamente a partir do cadastro. Publique a primeira aplicação e informe as tecnologias que ela usa.'
                    : 'Preencha o campo Tecnologias / Stacks no cadastro das aplicações para ver aqui a matriz por categoria, com contagem de uso e concentração de conhecimento.'
                }
                actionLabel="Cadastrar aplicação"
                onAction={() => navigate('/admin/applications/new')}
              />
              <CatalogBrowser dark={dark} />
            </>
          )}
          {state === 'matrix' && (
            <>
              {/* -------------------------------------------------- métricas */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: byViewport(vp, METRIC_GRID),
                  gap: byViewport(vp, { mobile: 10, tablet: 12, desktop: 14 }),
                  marginBottom: 18,
                }}
              >
                <MetricTile
                  dark={dark}
                  label="Tecnologias em uso"
                  icon={<IconCpu size={14} />}
                  iconColor={accent}
                  sub={`${base.links} vínculos com aplicações`}
                >
                  <MetricNumber value={base.techs.length} p={p} />
                </MetricTile>

                <MetricTile
                  dark={dark}
                  label="Categorias representadas"
                  icon={<IconLayers size={14} />}
                  iconColor="#2563eb"
                  sub={`de ${STACK_CATALOG.length} no catálogo de stacks`}
                >
                  <MetricNumber value={base.categoriesUsed} p={p} />
                </MetricTile>

                <MetricTile
                  dark={dark}
                  label="Mais usada"
                  icon={<IconTrend size={14} />}
                  iconColor="#16a34a"
                  sub={
                    base.top
                      ? `em ${base.top.apps.length} de ${apps.length} aplicações · ${base.top.category}`
                      : 'sem dados suficientes'
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      minWidth: 0,
                      height: 36,
                    }}
                  >
                    {base.top && (
                      <span
                        style={{
                          width: 11,
                          height: 11,
                          borderRadius: 3,
                          flexShrink: 0,
                          background: stackColor(base.top.name, dark),
                          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                        }}
                      />
                    )}
                    <span
                      style={{
                        fontSize: 19,
                        fontWeight: 600,
                        fontFamily: fontSans,
                        color: p.text,
                        letterSpacing: '-0.02em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {base.top?.name ?? 'sem dados'}
                    </span>
                  </div>
                </MetricTile>

                <MetricTile
                  dark={dark}
                  label="Conhecimento concentrado"
                  icon={<IconAlert size={14} />}
                  iconColor="#ca8a04"
                  sub="tecnologias usadas por apenas 1 aplicação"
                >
                  <MetricNumber value={base.singles} p={p} />
                </MetricTile>
              </div>

              {/* -------------------------------------------------- barra de filtros */}
              <div
                style={{
                  background: p.surface,
                  border: `1px solid ${p.border}`,
                  borderRadius: 10,
                  padding: byViewport(vp, { mobile: 12, tablet: 14, desktop: 14 }),
                  marginBottom: 18,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    // No celular busca, ordenação e visão viram três linhas: os
                    // dois segmentados pedem 376px juntos e não caberiam ao lado
                    // de uma busca útil. O espaçador `flex: 1` fica inofensivo em
                    // coluna (sem altura livre para crescer).
                    flexDirection: byViewport(vp, TOOLBAR_DIR),
                    alignItems: byViewport(vp, TOOLBAR_ALIGN),
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      height: byViewport(vp, SEARCH_H),
                      padding: '0 10px',
                      background: p.inputBg,
                      border: `1px solid ${p.border}`,
                      borderRadius: 7,
                      width: byViewport(vp, SEARCH_W),
                      color: p.muted,
                    }}
                  >
                    <IconSearch size={13} />
                    <input
                      value={query}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setQuery(e.target.value)
                      }
                      placeholder="Buscar tecnologia ou aplicação…"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        // Campo esticado = a caixa inteira é área de toque; sem
                        // isso o alvo real do teclado tinha 18px de altura.
                        alignSelf: byViewport(vp, {
                          mobile: 'stretch',
                          tablet: 'stretch',
                          desktop: 'auto',
                        }),
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        fontSize: 12,
                        color: p.text,
                        fontFamily: fontSans,
                      }}
                    />
                    {searching && (
                      <button
                        type="button"
                        onClick={() => setQuery('')}
                        title="Limpar busca"
                        aria-label="Limpar busca"
                        style={{
                          display: 'inline-flex',
                          alignItems: byViewport(vp, COMPACT_CENTER),
                          justifyContent: byViewport(vp, COMPACT_CENTER),
                          width: byViewport(vp, CLEAR_BTN),
                          height: byViewport(vp, CLEAR_BTN),
                          flexShrink: byViewport(vp, COMPACT_FIRM),
                          background: 'transparent',
                          border: 'none',
                          color: p.muted,
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <IconX size={12} />
                      </button>
                    )}
                  </div>

                  <div style={{ flex: 1 }} />

                  <Segmented
                    dark={dark}
                    value={sort}
                    onChange={setSort}
                    options={[
                      { id: 'usage', label: view === 'tech' ? 'Mais usadas' : 'Mais tecnologias' },
                      { id: 'alpha', label: 'A-Z' },
                    ]}
                  />
                  <Segmented
                    dark={dark}
                    value={view}
                    onChange={setView}
                    options={[
                      { id: 'tech', label: 'Por tecnologia' },
                      { id: 'app', label: 'Por aplicação' },
                    ]}
                  />
                </div>

                <div style={{ height: 1, background: p.border }} />

                {/* No celular o rótulo sobe e os chips ficam com a linha toda: a
                    coluna de 86px deixava 146px para chips de até 186px — era o
                    estouro clássico de fileira de chips. */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: byViewport(vp, FACET_DIR),
                    gap: byViewport(vp, FACET_GAP),
                    alignItems: 'flex-start',
                  }}
                >
                  <SectionLabel p={p}>Categoria</SectionLabel>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                      flex: 1,
                      minWidth: 0,
                      alignSelf: byViewport(vp, COMPACT_STRETCH),
                    }}
                  >
                    <FilterChip
                      label="Todas"
                      count={result.categoryTotal}
                      active={category === 'all'}
                      onClick={() => setCategory('all')}
                      dark={dark}
                      accent={accent}
                    />
                    {CATEGORY_LABELS.filter((l) => (result.categoryCounts.get(l) ?? 0) > 0).map(
                      (label) => (
                        <FilterChip
                          key={label}
                          label={label}
                          count={result.categoryCounts.get(label) ?? 0}
                          active={category === label}
                          onClick={() => setCategory(category === label ? 'all' : label)}
                          dark={dark}
                          accent={accent}
                        />
                      ),
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: byViewport(vp, FACET_DIR),
                    gap: byViewport(vp, FACET_GAP),
                    alignItems: 'flex-start',
                  }}
                >
                  <SectionLabel p={p}>Aplicação</SectionLabel>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                      flex: 1,
                      minWidth: 0,
                      alignItems: 'center',
                      // Em coluna, `flex: 1` deixa de dar largura: sem isto a
                      // fileira de chips encolheria até o menor chip.
                      alignSelf: byViewport(vp, COMPACT_STRETCH),
                    }}
                  >
                    <FilterChip
                      label="Todos os tipos"
                      count={result.typeTotal}
                      active={type === 'all'}
                      onClick={() => setType('all')}
                      dark={dark}
                      accent={accent}
                    />
                    {typeChips.map((id) => (
                      <FilterChip
                        key={id}
                        label={APP_TYPES[id]?.label ?? id}
                        count={result.typeCounts.get(id) ?? 0}
                        active={type === id}
                        onClick={() => setType(type === id ? 'all' : id)}
                        dark={dark}
                        accent={accent}
                      />
                    ))}
                    <div style={{ width: 1, height: 22, background: p.border, margin: '0 4px' }} />
                    <FilterChip
                      label="Toda criticidade"
                      count={result.criticalityTotal}
                      active={criticality === 'all'}
                      onClick={() => setCriticality('all')}
                      dark={dark}
                      accent={accent}
                    />
                    {criticalityChips.map((c) => (
                      <FilterChip
                        key={c.id}
                        label={c.label}
                        count={result.criticalityCounts.get(c.id) ?? 0}
                        active={criticality === c.id}
                        onClick={() => setCriticality(criticality === c.id ? 'all' : c.id)}
                        dark={dark}
                        accent={accent}
                      />
                    ))}
                  </div>
                </div>

                {hasFilters && (
                  <>
                    <div style={{ height: 1, background: p.border }} />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: p.muted,
                          marginRight: 2,
                        }}
                      >
                        Filtros ativos
                      </span>
                      {searching && (
                        <ActivePill
                          dark={dark}
                          accent={accent}
                          label="Busca"
                          value={query.trim()}
                          onClear={() => setQuery('')}
                        />
                      )}
                      {category !== 'all' && (
                        <ActivePill
                          dark={dark}
                          accent={accent}
                          label="Categoria"
                          value={category}
                          onClear={() => setCategory('all')}
                        />
                      )}
                      {type !== 'all' && (
                        <ActivePill
                          dark={dark}
                          accent={accent}
                          label="Tipo"
                          value={APP_TYPES[type]?.label ?? type}
                          onClear={() => setType('all')}
                        />
                      )}
                      {criticality !== 'all' && (
                        <ActivePill
                          dark={dark}
                          accent={accent}
                          label="Criticidade"
                          value={criticalityLabel(criticality)}
                          onClear={() => setCriticality('all')}
                        />
                      )}
                      <div style={{ flex: 1 }} />
                      <ButtonGhost dark={dark} onClick={clearFilters}>
                        <IconX size={12} /> Limpar filtros
                      </ButtonGhost>
                    </div>
                  </>
                )}
              </div>

              {/* -------------------------------------------------- resultado */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 12,
                  // "13 de 13 tecnologias · 13 vínculos" já toma 238 dos 242px de
                  // um celular de 320px: o "Recolher tudo" desce de linha.
                  flexWrap: byViewport(vp, CARD_HEAD_WRAP),
                }}
              >
                <span style={{ fontSize: 12, color: p.muted, ...numeroTabular }}>
                  {view === 'tech'
                    ? `${result.visible.length} de ${base.techs.length} tecnologias · ${result.links} vínculos`
                    : `${result.appRows.length} de ${apps.length} aplicações`}
                </span>
                <div style={{ flex: 1 }} />
                {view === 'tech' && result.sections.length > 1 && !searching && (
                  <ButtonGhost
                    dark={dark}
                    onClick={() =>
                      setCollapsed(
                        allCollapsed
                          ? new Set<string>()
                          : new Set(result.sections.map((s) => s.label)),
                      )
                    }
                  >
                    {allCollapsed ? 'Expandir tudo' : 'Recolher tudo'}
                  </ButtonGhost>
                )}
              </div>

              {view === 'tech' ? (
                <TechMatrix
                  sections={result.sections}
                  max={base.max}
                  dark={dark}
                  accent={accent}
                  searching={searching}
                  collapsedLabels={collapsed}
                  onToggleSection={toggleSection}
                  onClearFilters={clearFilters}
                  onOpenApp={(a) => navigate(`/app/${a.slug}`)}
                />
              ) : (
                <AppMatrix
                  rows={result.appRows}
                  dark={dark}
                  accent={accent}
                  onClearFilters={clearFilters}
                  onOpenApp={(a) => navigate(`/app/${a.slug}`)}
                  onPickTech={(name) => {
                    setQuery(name);
                    setView('tech');
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
